import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { ArrowLeftIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Field } from '~/components/shared/Field';
interface ProductVariant {
  id: string;
  title: string;
  price: string;
  currency: string;
  inventory: string;
  image?: File | null;
  imageURL?: string;
}

interface ProductFormData {
  title: string;
  description: string;
  price: string;
  currency: string;
  inventory: string;
  featuredImage?: File | null;
  videoUrl?: string;
  expirationDate?: string;
}

const Product: React.FC = () => {
  const navigate = useNavigate();
  const { agentId } = useParams();
  const location = useLocation();
  const [t] = useTranslation('store');
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [mainImage, setMainImage] = useState<File | null>(null);
  const [variantImagePreviews, setVariantImagePreviews] = useState<{[key: string]: string}>({});
  const [showToastAlert, setShowToastAlert] = useState(false);
  const [showToastSuccess, setShowToastSuccess] = useState(false);
  const [message, setMessage] = useState('');
  
  // Check if this is the first product (from query parameter)
  const [isFirstProduct, setIsFirstProduct] = useState(false);
  
  useEffect(() => {
    // Extract the 'first' query parameter
    const searchParams = new URLSearchParams(location.search);
    const firstParam = searchParams.get('first');
    setIsFirstProduct(firstParam === 'true');
  }, [location.search]);
  
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<ProductFormData>({
    defaultValues: {
      currency: 'COP'
    }
  });

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleImageUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        setImagePreview(e.target.result as string);
        setMainImage(file);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleVariantImageUpload = (variantId: string, file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        setVariantImagePreviews(prev => ({
          ...prev,
          [variantId]: e.target!.result as string
        }));
        updateVariant(variantId, 'image', file);
      }
    };
    reader.readAsDataURL(file);
  };

  const addVariant = () => {
    const newVariant: ProductVariant = {
      id: `variant-${Date.now()}`,
      title: '',
      price: '',
      currency: 'COP',
      inventory: '',
      image: null
    };
    setVariants([...variants, newVariant]);
  };

  const removeVariant = (variantId: string) => {
    setVariants(variants.filter(variant => variant.id !== variantId));
    // Remove the image preview for this variant
    setVariantImagePreviews(prev => {
      const newPreviews = { ...prev };
      delete newPreviews[variantId];
      return newPreviews;
    });
  };

  const updateVariant = (variantId: string, field: keyof ProductVariant, value: string | File | null) => {
    setVariants(variants.map(variant => 
      variant.id === variantId 
        ? { ...variant, [field]: value }
        : variant
    ));
  };

  const showToastWarning = (mensaje: string, isWarning: boolean) => {
    if (isWarning) setShowToastAlert(true);
    else setShowToastSuccess(true);
    setMessage(mensaje);
    setTimeout(() => {
      if (isWarning) setShowToastAlert(false);
      else setShowToastSuccess(false);
    }, 3000);
  };

  const onSubmit = async (data: ProductFormData) => {
    try {
      // Validación básica
      if (!agentId) {
        showToastWarning('ID del agente no disponible', true);
        return;
      }

      // Validar que las variantes adicionales estén completas si existen
      if (variants.length > 0) {
        const incompleteVariants = variants.filter(variant => 
          !variant.title.trim() || !variant.price || !variant.currency || !variant.inventory
        );
        
        if (incompleteVariants.length > 0) {
          showToastWarning('Por favor completa todos los campos de las variantes antes de guardar', true);
          return;
        }
      }

      // Crear FormData para enviar archivos
      const formData = new FormData();
      
      // Agregar datos básicos del producto (estos irán a la variante principal)
      formData.append('agent_id', agentId);
      formData.append('title', data.title);
      formData.append('description', data.description);
      formData.append('price', data.price);
      formData.append('currency', data.currency);
      formData.append('inventory', data.inventory);
      
      // Añadir el flag isFirstProduct para indicar si es el primer producto
      formData.append('isFirstProduct', isFirstProduct ? 'true' : 'false');
      
      // Obtener solo el código de idioma principal (es, en) de navigator.language
      const languageCode = navigator.language ? navigator.language.split('-')[0] : 'es';
      formData.append('language', languageCode === 'en' ? 'en' : 'es'); // Por defecto 'es' si no es inglés
      
      if (data.videoUrl) {
        formData.append('videoUrl', data.videoUrl);
      }
      
      if (data.expirationDate) {
        formData.append('expirationDate', data.expirationDate);
      }

      // Agregar imagen principal si existe (irá a la variante principal)
      if (mainImage) {
        formData.append('files', mainImage);
      }

      // Agregar imágenes de variantes adicionales en orden
      variants.forEach((variant) => {
        if (variant.image) {
          formData.append('files', variant.image);
        }
      });

      // Agregar variantes adicionales (la variante principal se crea automáticamente en el backend)
      if (variants.length > 0) {
        // Enviar solo las variantes adicionales (la principal se crea automáticamente)
        const additionalVariants = variants.map((variant) => ({
          title: variant.title || '',
          price: variant.price || '0',
          currency: variant.currency || 'COP',
          inventory: variant.inventory || '0',
          // Incluir imageURL si existe (para imágenes ya guardadas)
          ...(variant.imageURL && { image: variant.imageURL })
        }));
        
        formData.append('variants', JSON.stringify(additionalVariants));
      } else {
        // Enviar array vacío si no hay variantes adicionales
        formData.append('variants', JSON.stringify([]));
      }

      // Realizar la petición a la API de creación
      const response = await fetch(`${import.meta.env.VITE_API_URL}/products/createProducts`, {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        }
      });

      const result = await response.json();

      if (response.ok && !result.hasError) {
        const successMessage = isFirstProduct 
          ? '¡Felicidades! Tu primer producto ha sido creado exitosamente' 
          : 'Producto creado exitosamente';
          
        showToastWarning(successMessage, false);
        // Redirigir de vuelta al inventario después de un breve delay
        setTimeout(() => {
          navigate(`/agent/${agentId}/store`);
        }, 500);
      } else {
        showToastWarning(result.message || 'Error al crear el producto', true);
      }
    } catch (error: any) {
      showToastWarning(error.message || 'Error al crear el producto', true);
    }
  };

  return (
    <div className="space-y-6 w-full">
      {/* Header con botón de regresar */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleBackClick}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          {t('back_to_inventory')}
        </button>
        
        
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Contenido principal */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-6">
            {/* Imagen del producto */}
            <div className="max-w-full form-control my-2 flex flex-col gap-2">
              <div className="label flex items-center justify-between">
                <span className="label-text font-bold">Imagen del producto</span>
              </div>
              <div className="mt-1">
                <div
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.onchange = (e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (file) {
                        handleImageUpload(file);
                      }
                    };
                    input.click();
                  }}
                  className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 border-dashed border-gray-300 hover:border-gray-400 cursor-pointer transition-colors"
                >
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                      <svg className="h-12 w-12 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-sm text-center">Hacer clic para subir imagen</p>
                    </div>
                  )}
                  {imagePreview && (
                    <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all flex items-center justify-center">
                      <svg className="h-8 w-8 text-white opacity-0 hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Información del producto */}
            <div className="space-y-6">
              <Field
                register={register}
                label={t('product.title') || 'Título del producto'}
                fieldName="title"
                watch={watch}
                placeholder="Ingresa el título del producto"
                options={{ required: 'Este campo es requerido' }}
                textError={errors.title?.message}
              />

              {/* Precio y Moneda en la misma línea */}
              <div className="grid grid-cols-2 gap-3">
                {/* Campo Precio */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('product.price') || 'Precio'}
                  </label>
                  <input
                    type="number"
                    {...register('price', { required: 'Este campo es requerido', min: { value: 0, message: 'El precio debe ser mayor a 0' } })}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    onWheel={(e) => e.currentTarget.blur()}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {errors.price && (
                    <p className="mt-1 text-sm text-red-600">{errors.price.message}</p>
                  )}
                </div>
                
                {/* Campo Moneda */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('product.currency') || 'Moneda'}
                  </label>
                  <select
                    {...register('currency', { required: 'Este campo es requerido' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="COP">{t('product.currencies.COP') || 'COP - Peso Colombiano'}</option>
                    <option value="USD">{t('product.currencies.USD') || 'USD - Dólar Americano'}</option>
                    <option value="EUR">{t('product.currencies.EUR') || 'EUR - Euro'}</option>
                    <option value="GBP">{t('product.currencies.GBP') || 'GBP - Libra Esterlina'}</option>
                    <option value="CNY">{t('product.currencies.CNY') || 'CNY - Yuan Chino'}</option>
                  </select>
                  {errors.currency && (
                    <p className="mt-1 text-sm text-red-600">{errors.currency.message}</p>
                  )}
                </div>
              </div>

              <Field
                register={register}
                label={t('product.inventory') || 'Inventario'}
                fieldName="inventory"
                type="text"
                watch={watch}
                placeholder="0"
                options={{ 
                  required: 'Este campo es requerido', 
                  pattern: { 
                    value: /^[0-9]*$/, 
                    message: 'Solo se permiten números' 
                  } 
                }}
                textError={errors.inventory?.message}
              />

              <Field
                register={register}
                label="URL del video (Opcional)"
                fieldName="videoUrl"
                watch={watch}
                placeholder="https://youtube.com/watch?v=..."
                tooltip="Enlace a un video promocional del producto (YouTube, Vimeo, etc.)"
              />

              <Field
                register={register}
                label="Fecha de expiración (Opcional)"
                fieldName="expirationDate"
                type="date"
                watch={watch}
                tooltip="Fecha hasta cuando estará disponible el producto"
              />
            </div>
          </div>

          {/* Descripción */}
          <div className="px-6 pb-6">
            <Field
              register={register}
              label={t('product.description') || 'Descripción'}
              fieldName="description"
              type="textarea"
              watch={watch}
              placeholder="Describe tu producto..."
              options={{ required: 'Este campo es requerido' }}
              textError={errors.description?.message}
              className="max-w-full"
            />
          </div>
        </div>

        {/* Variantes adicionales del producto */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                {t('product_variants') || 'Variantes adicionales del producto'}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                La información principal del producto se guarda automáticamente como variante principal. Aquí puedes agregar variantes adicionales como diferentes tamaños, colores, etc.
              </p>
            </div>
            <button
              type="button"
              onClick={addVariant}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              <PlusIcon className="h-5 w-5" />
              Agregar variante
            </button>
          </div>

          {variants.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>No hay variantes adicionales. El producto se creará con una variante principal usando la información básica.</p>
              <p className="text-sm mt-2">Haz clic en "Agregar variante" para crear variantes adicionales como diferentes tamaños o colores.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {variants.map((variant, index) => (
                <div key={variant.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium text-gray-900">Variante adicional #{index + 1}</h4>
                    <button
                      type="button"
                      onClick={() => removeVariant(variant.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    {/* Imagen de la variante */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Imagen
                      </label>
                      <div 
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.onchange = (e) => {
                            const file = (e.target as HTMLInputElement).files?.[0];
                            if (file) {
                              handleVariantImageUpload(variant.id, file);
                            }
                          };
                          input.click();
                        }}
                        className="relative w-16 h-16 bg-gray-100 rounded-lg overflow-hidden border-2 border-dashed border-gray-300 hover:border-gray-400 cursor-pointer transition-colors flex items-center justify-center"
                      >
                        {variantImagePreviews[variant.id] ? (
                          <img
                            src={variantImagePreviews[variant.id]}
                            alt="Variant preview"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-col">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nombre de la variante
                      </label>
                      <input
                        type="text"
                        value={variant.title}
                        onChange={(e) => updateVariant(variant.id, 'title', e.target.value)}
                        placeholder="Ej: Talla M, Color Rojo"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Precio
                      </label>
                      <input
                        type="number"
                        value={variant.price}
                        onChange={(e) => updateVariant(variant.id, 'price', e.target.value)}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        onWheel={(e) => e.currentTarget.blur()}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('product.currency') || 'Moneda'}
                      </label>
                      <select
                        value={variant.currency}
                        onChange={(e) => updateVariant(variant.id, 'currency', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="COP">{t('product.currencies.COP') || 'COP - Peso Colombiano'}</option>
                        <option value="USD">{t('product.currencies.USD') || 'USD - Dólar Americano'}</option>
                        <option value="EUR">{t('product.currencies.EUR') || 'EUR - Euro'}</option>
                        <option value="GBP">{t('product.currencies.GBP') || 'GBP - Libra Esterlina'}</option>
                        <option value="CNY">{t('product.currencies.CNY') || 'CNY - Yuan Chino'}</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Inventario
                      </label>
                      <input
                        type="text"
                        value={variant.inventory}
                        onChange={(e) => updateVariant(variant.id, 'inventory', e.target.value.replace(/[^0-9]/g, ''))}
                        placeholder="0"
                        onWheel={(e) => e.currentTarget.blur()}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Botones de acción */}
        <div className="flex items-center justify-end gap-4">
          <button
            type="button"
            onClick={handleBackClick}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Guardando...' : 'Guardar producto'}
          </button>
        </div>
      </form>

      {/* Toast notifications */}
      {showToastAlert && (
        <div className="toast-button toast toast-center w-80">
          <div className="alert alert-warning">
            <span>{message}</span>
          </div>
        </div>
      )}

      {showToastSuccess && (
        <div className="toast-button toast toast-center w-80">
          <div className="alert alert-success">
            <span>{message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Product;
